import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/app/providers";
import { countUnreadInbox } from "@/features/messaging/mailboxFolders";
import { mapApiConversationsToMailboxConversations } from "@/features/messaging/mailboxConversations";
import { fetchConversations } from "@/features/messaging/lib/messagingApi";
import { extractWsMessage } from "@/features/messaging/lib/wsMessageMapper";

type MessagingUnreadContextValue = {
  unreadCount: number;
  reportUnreadCount: (count: number) => void;
};

const MessagingUnreadContext = createContext<MessagingUnreadContextValue | null>(
  null,
);

export function MessagingUnreadProvider({ children }: { children: ReactNode }) {
  const { token, currentBuilding, user } = useAuth();
  const userId = user?.pk ?? null;
  const location = useLocation();
  const onMessageriePage = location.pathname.startsWith("/messagerie");
  const [unreadCount, setUnreadCount] = useState(0);

  const reportUnreadCount = useCallback((count: number) => {
    setUnreadCount(Math.max(0, count));
  }, []);

  const refreshFromApi = useCallback(async () => {
    const buildingId = currentBuilding?.pk;
    if (!token || buildingId == null) {
      setUnreadCount(0);
      return;
    }
    try {
      const rows = await fetchConversations(token, buildingId);
      const mailboxConversations = mapApiConversationsToMailboxConversations(rows, userId);
      setUnreadCount(countUnreadInbox(mailboxConversations));
    } catch {
      /* conserve le dernier compteur connu */
    }
  }, [token, currentBuilding?.pk, userId]);

  useEffect(() => {
    void refreshFromApi();
  }, [refreshFromApi]);

  useEffect(() => {
    if (onMessageriePage) return;

    const onReceive = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      const raw = extractWsMessage(detail ?? {});
      if (!raw) return;
      const senderId = raw.sender ?? raw.senderUserId;
      if (userId != null && senderId === userId) return;
      if (raw.has_been_read) return;
      setUnreadCount((prev) => prev + 1);
    };

    window.addEventListener("messaging:messageReceive", onReceive);
    return () => window.removeEventListener("messaging:messageReceive", onReceive);
  }, [onMessageriePage, userId]);

  const value = useMemo(
    () => ({ unreadCount, reportUnreadCount }),
    [unreadCount, reportUnreadCount],
  );

  return (
    <MessagingUnreadContext.Provider value={value}>
      {children}
    </MessagingUnreadContext.Provider>
  );
}

export function useMessagingUnread(): MessagingUnreadContextValue {
  const ctx = useContext(MessagingUnreadContext);
  if (!ctx) {
    throw new Error("useMessagingUnread must be used within MessagingUnreadProvider");
  }
  return ctx;
}
